import React, { useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  LocalHospital,
  Dashboard,
  CalendarMonth,
  Person,
  People,
  Receipt,
  Settings,
  ExitToApp,
  Info,
  MedicalServices,
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';

const Header = () => {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    handleCloseUserMenu();
  };

  // Navigation links based on user role
  const getNavigationLinks = () => {
    if (!isAuthenticated) {
      return [
        { title: 'صفحه اصلی', path: '/' },
        { title: 'پزشکان', path: '/doctors' },
        { title: 'درباره ما', path: '/about' },
      ];
    }

    if (user?.role === 'admin') {
      return [
        { title: 'داشبورد', path: '/admin/dashboard' },
        { title: 'پزشکان', path: '/admin/doctors' },
        { title: 'بیماران', path: '/admin/patients' },
        { title: 'قرارملاقات ها', path: '/admin/appointments' },
      ];
    }

    if (user?.role === 'doctor') {
      return [
        { title: 'داشبورد', path: '/doctor/dashboard' },
        { title: 'قرارملاقات ها', path: '/doctor/appointments' },
        { title: 'زمان بندی', path: '/doctor/schedule' },
        { title: 'بیماران', path: '/doctor/patients' },
      ];
    }

    return [
      { title: 'داشبورد', path: '/dashboard' },
      { title: 'پزشکان', path: '/doctors' },
      { title: 'قرارملاقات ها', path: '/appointments' },
    ];
  };

  // Drawer menu items based on user role
  const getDrawerItems = () => {
    const items = getNavigationLinks();

    return [
      ...items,
      { title: 'پروفایل', path: user?.role === 'doctor' ? '/doctor/profile' : (user?.role === 'admin' ? '/admin/profile' : '/profile') },
    ];
  };

  // User menu items
  const getUserMenuItems = () => {
    return [
      { title: 'پروفایل', path: user?.role === 'doctor' ? '/doctor/profile' : (user?.role === 'admin' ? '/admin/profile' : '/profile') },
      { title: 'تنظیمات', path: '/settings' },
      { title: 'خروج', onClick: handleLogout },
    ];
  };

  // Get icon for drawer items
  const getDrawerItemIcon = (title) => {
    switch (title) {
      case 'داشبورد':
        return <Dashboard />;
      case 'پزشکان':
        return <MedicalServices />;
      case 'بیماران':
        return <People />;
      case 'قرارملاقات ها':
        return <CalendarMonth />;
      case 'زمان بندی':
        return <Receipt />;
      case 'پروفایل':
        return <Person />;
      case 'تنظیمات':
        return <Settings />;
      case 'درباره ما':
        return <Info />;
      case 'صفحه اصلی':
      default:
        return <Dashboard />;
    }
  };

  return (
    <AppBar position="sticky" sx={{ boxShadow: 2 }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo - Desktop */}
          <LocalHospital sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            سیستم نوبت دهی پزشکی
          </Typography>

          {/* Mobile Menu Icon */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="منوی اصلی"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={() => setDrawerOpen(true)}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Logo - Mobile */}
          <LocalHospital sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            نوبت دهی پزشکی
          </Typography>

          {/* Desktop Navigation Links */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {getNavigationLinks().map((link) => (
              <Button
                key={link.path}
                component={Link}
                to={link.path}
                sx={{
                  mx: 1,
                  color: 'white',
                  position: 'relative',
                  '&::after': location.pathname === link.path
                    ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '3px',
                        backgroundColor: 'white',
                      }
                    : {},
                }}
              >
                {link.title}
              </Button>
            ))}
          </Box>

          {/* User Menu */}
          <Box sx={{ flexGrow: 0 }}>
            {isAuthenticated ? (
              <>
                <Tooltip title="تنظیمات کاربری">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar src="/static/avatar/user.jpg" alt={user?.name}>
                      {user?.name?.charAt(0) || 'U'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {user?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.email}
                    </Typography>
                  </Box>
                  <Divider />
                  {getUserMenuItems().map((item) => (
                    <MenuItem
                      key={item.title}
                      onClick={() => {
                        handleCloseUserMenu();
                        if (item.onClick) {
                          item.onClick();
                        } else if (item.path) {
                          navigate(item.path);
                        }
                      }}
                    >
                      <Typography textAlign="center">{item.title}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/login"
                  color="inherit"
                  sx={{ mx: 1 }}
                >
                  ورود
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  color="secondary"
                  sx={{ mx: 1 }}
                >
                  ثبت نام
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250, pt: 2 }} role="presentation">
          <List>
            {getDrawerItems().map((item) => (
              <ListItem
                button
                key={item.title}
                onClick={() => {
                  navigate(item.path);
                  setDrawerOpen(false);
                }}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{getDrawerItemIcon(item.title)}</ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItem>
            ))}
          </List>
          <Divider />
          {isAuthenticated && (
            <List>
              <ListItem
                button
                onClick={() => {
                  handleLogout();
                  setDrawerOpen(false);
                }}
              >
                <ListItemIcon>
                  <ExitToApp />
                </ListItemIcon>
                <ListItemText primary="خروج" />
              </ListItem>
            </List>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;